import { useState } from "react";
import { toast } from "sonner";
import { Lock, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EntityPicker, itemRender, ITEM_URL } from "@/components/app/EntityPicker";
import { useMe } from "@/components/app/MeContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";

const EST = [["estimate_no", "Estimate no.", "text"], ["estimate_date", "Estimate date", "date"], ["currency", "Currency", "text"],
  ["estimated_value", "Estimated value", "number"], ["validity_days", "Validity (days)", "number"]];

const toForm = (rev) => ({
  estimate: { ...rev.estimate, estimated_value: rev.estimate.estimated_value ?? "", validity_days: rev.estimate.validity_days ?? "" },
  bom: rev.bom.map((b) => ({ ...b, key: crypto.randomUUID(), item: b.item_code ? { ...b.item_snapshot, item_code: b.item_code } : null, qty: String(b.qty) })),
  change_note: rev.change_note || "",
});

export function RevisionEditor({ design, rev, onSaved }) {
  const { can } = useMe();
  const locked = rev.status !== "draft" || !can("write");
  const [f, setF] = useState(() => toForm(rev));
  const [busy, setBusy] = useState(false);
  const setE = (k) => (e) => setF({ ...f, estimate: { ...f.estimate, [k]: e.target.value } });
  const setB = (i, patch) => setF({ ...f, bom: f.bom.map((b, j) => (j === i ? { ...b, ...patch } : b)) });

  const persist = async () => {
    const e = f.estimate;
    await api.put(`/designs/${design.id}/revisions/${rev.id}`, {
      estimate: { ...e, estimated_value: e.estimated_value === "" ? null : Number(e.estimated_value), validity_days: e.validity_days === "" ? null : Number(e.validity_days) },
      bom: f.bom.map((b) => ({ item_code: b.item?.item_code || null, description: b.description, qty: Number(b.qty), remarks: b.remarks })),
      change_note: f.change_note,
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      await persist();
      toast.success(`R${rev.revision_no} saved`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const approve = async () => {
    if (!window.confirm(`Save and approve R${rev.revision_no}? It will be locked and can be linked to orders.`)) return;
    try { if (can("write")) await persist(); await api.post(`/designs/${design.id}/revisions/${rev.id}/approve`); toast.success("Revision approved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid={`revision-editor-R${rev.revision_no}`}>
      {rev.status === "rejected" && (
        <div className="text-xs flex items-center gap-2 text-red-800 bg-red-50 border border-red-200 rounded p-2" data-testid="revision-rejected-note">
          <Lock className="h-4 w-4" /> Rejected by {rev.rejected_by?.name} · {fmtDateTime(rev.rejected_at)} — {rev.reject_reason}. Create a new revision to try again.
        </div>
      )}
      {rev.status === "approved" && (
        <div className="text-xs flex items-center gap-2 text-blue-800 bg-blue-50 border border-blue-200 rounded p-2" data-testid="revision-locked-note">
          <Lock className="h-4 w-4" /> Approved by {rev.approved_by?.name} · {fmtDateTime(rev.approved_at)}. Locked — create a new revision to change it.
        </div>
      )}
      <div>
        <h4 className="text-sm font-semibold mb-2">Estimate header</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {EST.map(([k, l, t]) => <div key={k}><Label>{l}</Label><Input type={t} disabled={locked} value={f.estimate[k] ?? ""} onChange={setE(k)} data-testid={`estimate-${k}-input`} /></div>)}
          <div className="sm:col-span-3"><Label>Scope notes</Label><Textarea disabled={locked} rows={2} value={f.estimate.scope_notes || ""} onChange={setE("scope_notes")} data-testid="estimate-scope-input" /></div>
        </div>
        {locked && f.estimate.estimated_value !== "" && <div className="text-xs text-slate-500 mt-1 font-mono">{inr(f.estimate.estimated_value)}</div>}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">BOM (placeholder)</h4>
          {!locked && <Button size="sm" variant="outline" onClick={() => setF({ ...f, bom: [...f.bom, { key: crypto.randomUUID(), item: null, description: "", qty: "1", remarks: "" }] })} data-testid="bom-add-line-btn"><Plus className="h-4 w-4 mr-1" />Add BOM line</Button>}
        </div>
        <div className="space-y-2">
          {f.bom.map((b, i) => (
            <div key={b.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`bom-line-${i}`}>
              <div className="md:col-span-4">{locked ? <span className="font-mono text-xs">{b.item?.item_code || "free text"}</span> :
                <EntityPicker url={ITEM_URL} value={b.item} onChange={(p) => setB(i, { item: p, description: p ? p.item_name : b.description })} render={itemRender} placeholder="Item (optional)" testid={`bom-part-picker-${i}`} />}</div>
              <Input className="md:col-span-4" disabled={locked} placeholder="Description" value={b.description} onChange={(e) => setB(i, { description: e.target.value })} data-testid={`bom-description-${i}`} />
              <Input className="md:col-span-1" type="number" disabled={locked} value={b.qty} onChange={(e) => setB(i, { qty: e.target.value })} data-testid={`bom-qty-${i}`} />
              <Input className="md:col-span-2" disabled={locked} placeholder="Remarks" value={b.remarks || ""} onChange={(e) => setB(i, { remarks: e.target.value })} data-testid={`bom-remarks-${i}`} />
              {!locked && <Button variant="ghost" size="icon" onClick={() => setF({ ...f, bom: f.bom.filter((_, j) => j !== i) })} data-testid={`bom-remove-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
            </div>
          ))}
          {f.bom.length === 0 && <p className="text-sm text-slate-500">No BOM lines.</p>}
        </div>
      </div>
      <div><Label>Change note</Label><Input disabled={locked} value={f.change_note} onChange={(e) => setF({ ...f, change_note: e.target.value })} placeholder="What changed in this revision" data-testid="revision-change-note-input" /></div>
      {rev.status === "draft" && (
        <div className="flex gap-2">
          {can("write") && <Button onClick={save} disabled={busy} data-testid="revision-save-btn">{busy ? "Saving…" : "Save revision"}</Button>}
          {can("approve") && <Button variant="outline" onClick={approve} data-testid="revision-approve-btn">Approve R{rev.revision_no}</Button>}
        </div>
      )}
    </div>
  );
}
